import React, { Component } from 'react';
import { bool, func, instanceOf, object, oneOfType, shape, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import routeConfiguration from '../../routeConfiguration';
import { pathByRouteName, findRouteByRouteName } from '../../util/routes';
import { propTypes, DATE_TYPE_DATE } from '../../util/types';
import {
  ensureListing,
  ensureCurrentUser,
  ensureUser,
  ensureTransaction,
  ensureBooking,
  ensureStripeCustomer,
  ensurePaymentMethodCard,
} from '../../util/data';
import { dateFromLocalToAPI, minutesBetween } from '../../util/dates';
import { createSlug } from '../../util/urlHelpers';
import {
  isTransactionInitiateAmountTooLowError,
  isTransactionInitiateListingNotFoundError,
  isTransactionInitiateMissingStripeAccountError,
  isTransactionInitiateBookingTimeNotAvailableError,
  isTransactionChargeDisabledError,
  isTransactionZeroPaymentError,
  transactionInitiateOrderStripeErrors,
} from '../../util/errors';
import { formatMoney } from '../../util/currency';
import ReactHtmlParser from 'react-html-parser';
import { decode } from 'html-entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { TRANSITION_ENQUIRE, txIsPaymentPending, txIsPaymentExpired } from '../../util/transaction';
import {
  AvatarMedium,
  BookingBreakdown,
  NamedLink,
  NamedRedirect,
  Page,
} from '../../components';
import { marketplaceLockupLogoDarkText } from '../../assets/viveka-logos';
import { StripePaymentForm } from '../../forms';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { handleCardPayment, retrievePaymentIntent } from '../../ducks/stripe.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';

import {
  initiateOrder,
  setInitialValues,
  speculateTransaction,
  stripeCustomer,
  confirmPayment,
  sendMessage,
  validateCoupon,
} from './CheckoutPage.duck';
import { storeData, storedData, clearData } from './CheckoutPageSessionHelpers';
import css from './CheckoutPage.css';
import NavBarWrapper from '../../custom-components/wrappers/navbar-wrapper/NavBarWrapper';
import NavBar from '../../custom-components/elements/nav-bar/NavBar';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import ContainerWrapper from '../../custom-components/wrappers/container-wrapper/ContainerWrapper';
import Footer from '../../custom-components/elements/footer/Footer';

const STORAGE_KEY = 'CheckoutPage';

// Stripe PaymentIntent statuses, where user actions are already completed
// https://stripe.com/docs/payments/payment-intents/status
const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

const initializeOrderPage = (initialValues, routes, dispatch) => {
  const OrderPage = findRouteByRouteName('OrderDetailsPage', routes);

  // Transaction is already created, but if the initial message
  // sending failed, we tell it to the OrderDetailsPage.
  dispatch(OrderPage.setInitialValues(initialValues));
};

const checkIsPaymentExpired = existingTransaction => {
  return txIsPaymentExpired(existingTransaction)
    ? true
    : txIsPaymentPending(existingTransaction)
    ? minutesBetween(existingTransaction.attributes.lastTransitionedAt, new Date()) >= 15
    : false;
};

export class CheckoutPageComponent extends Component {
  bookingAlias;
  constructor(props) {
    super(props);

    this.state = {
      pageData: {},
      dataLoaded: false,
      submitting: false,
      localQuantity: 1,
      showFullDescription: false,
      showPromoInput: false,
      promoCode: '',
      promoApplying: false,
      promoError: null,
      appliedCoupon: null, // { code, coachId, discountType, discountValue }
    };
    this.stripe = null;

    this.onStripeInitialized = this.onStripeInitialized.bind(this);
    this.loadInitialData = this.loadInitialData.bind(this);
    this.handlePaymentIntent = this.handlePaymentIntent.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleQuantityChange = this.handleQuantityChange.bind(this);
    this.onApplyPromo = this.onApplyPromo.bind(this);
    this.onRemovePromo = this.onRemovePromo.bind(this);
  }

  // Shared by handleQuantityChange and onApplyPromo/onRemovePromo so the
  // price preview always reflects the currently applied coupon (or lack of
  // one) whenever quantity or the coupon changes.
  // Returns the { ok } result from the duck so callers can tell a
  // coupon-caused pricing failure apart from success (see onApplyPromo /
  // handleQuantityChange) instead of leaving the page stuck behind a
  // generic speculateTransactionError.
  refetchSpeculatedTransaction(couponInfo) {
    const { fetchSpeculatedTransaction } = this.props;
    const { pageData, localQuantity } = this.state;
    if (!(pageData && pageData.listing && pageData.bookingDates)) {
      return Promise.resolve({ ok: false });
    }

    const { bookingStart, bookingEnd } = pageData.bookingDates;
    return fetchSpeculatedTransaction(
      {
        listingId: pageData.listing.id,
        bookingStart: dateFromLocalToAPI(bookingStart),
        bookingEnd: dateFromLocalToAPI(bookingEnd),
        quantity: localQuantity,
      },
      this.bookingAlias,
      couponInfo
    );
  }

  // A coupon that validated fine on its own (active, applies to this
  // listing) can still make the actual price invalid — e.g. a fixed
  // discount that meets or exceeds the total for the current quantity.
  // Rather than leave speculateTransactionError set (which hides the whole
  // payment form), drop the coupon and re-fetch the plain price so
  // checkout stays usable.
  rejectCouponAndRefetch(message) {
    this.setState({ appliedCoupon: null, promoError: message });
    this.refetchSpeculatedTransaction(null);
  }

  onApplyPromo() {
    const { onValidateCoupon } = this.props;
    const { pageData } = this.state;
    const code = (this.state.promoCode || '').trim();
    if (!code) return;

    const currentListing = ensureListing(pageData.listing);
    const currentAuthor = ensureUser(currentListing.author);
    const coachId = currentAuthor.id && currentAuthor.id.uuid;
    const listingId = currentListing.id && currentListing.id.uuid;

    this.setState({ promoApplying: true, promoError: null });

    onValidateCoupon({ coachId, code, listingId }).then(result => {
      if (result && result.ok) {
        const appliedCoupon = {
          code,
          coachId,
          discountType: result.discountType,
          discountValue: result.discountValue,
        };
        this.setState({ promoApplying: false, promoError: null, appliedCoupon });
        this.refetchSpeculatedTransaction(appliedCoupon).then(result => {
          if (!result || !result.ok) {
            this.rejectCouponAndRefetch("That coupon can't be applied to this booking.");
          }
        });
      } else {
        const messages = {
          'not-found': 'That code is not valid.',
          expired: 'That code has expired.',
          inactive: 'That code is no longer active.',
          'redemption-limit-reached': 'That code has already been fully redeemed.',
          'not-applicable-to-listing': "That code doesn't apply to this listing.",
        };
        const errorKey = result && result.error;
        this.setState({
          promoApplying: false,
          promoError: messages[errorKey] || 'Could not apply that code.',
        });
      }
    });
  }

  onRemovePromo() {
    this.setState({ appliedCoupon: null, promoCode: '', promoError: null });
    this.refetchSpeculatedTransaction(null);
  }

  componentDidMount() {
    if (window) {
      this.loadInitialData();
    }
  }

  /**
   * Load initial data for the page
   *
   * Since the data for the checkout is not passed in the URL (there
   * might be lots of options in the future), we must pass in the data
   * some other way. Currently the ListingPage sets the initial data
   * for the CheckoutPage's Redux store.
   *
   * For some cases (e.g. a refresh in the CheckoutPage), the Redux
   * store is empty. To handle that case, we store the received data
   * to window.sessionStorage and read it from there if no props from
   * the store exist.
   *
   * This function also sets of fetching the speculative transaction
   * based on this initial data.
   */
  loadInitialData() {
    const {
      bookingData,
      bookingDates,
      listing,
      transaction,
      fetchSpeculatedTransaction,
      fetchStripeCustomer,
      history,

    } = this.props;

    // Fetch currentUser with stripeCustomer entity
    // Note: since there's need for data loading in "componentWillMount" function,
    //       this is added here instead of loadData static function.
    fetchStripeCustomer();

    // Browser's back navigation should not rewrite data in session store.
    // Action is 'POP' on both history.back() and page refresh cases.
    // Action is 'PUSH' when user has directed through a link
    // Action is 'REPLACE' when user has directed through login/signup process
    const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';

    const hasDataInProps = !!(bookingData && bookingDates && listing) && hasNavigatedThroughLink;
    if (hasDataInProps) {
      // Store data only if data is passed through props and user has navigated through a link.
      storeData(bookingData, bookingDates, listing, transaction, STORAGE_KEY);
    }

    // NOTE: stored data can be empty if user has already successfully completed transaction.
    const pageData = hasDataInProps
      ? { bookingData, bookingDates, listing, transaction }
      : storedData(STORAGE_KEY);

    const currentListing = ensureListing(pageData.listing);
    // console.log(currentListing);

    this.bookingAlias =
      currentListing &&
      currentListing.attributes.metadata &&
      currentListing.attributes.metadata.premium &&
      currentListing.attributes.metadata.premium > 0
        ? config.bookingProcessAlias.premium
        : config.bookingProcessAlias.classic;


    // const isVUPS = this.props.listing?.author?.attributes?.profile?.metadata?.isVUPS
    //
    // if(isVUPS){
    //   this.bookingAlias = config.bookingProcessAlias.transactionProcess3
    // }

    // console.log(this.bookingAlias);

    // Check if a booking is already created according to stored data.
    const tx = pageData ? pageData.transaction : null;
    const isBookingCreated = tx && tx.booking && tx.booking.id;

    const shouldFetchSpeculatedTransaction =
      pageData &&
      pageData.listing &&
      pageData.listing.id &&
      pageData.bookingData &&
      pageData.bookingDates &&
      pageData.bookingDates.bookingStart &&
      pageData.bookingDates.bookingEnd &&
      !isBookingCreated;

    if (shouldFetchSpeculatedTransaction) {
      const listingId = pageData.listing.id;
      const { bookingStart, bookingEnd } = pageData.bookingDates;

      // Convert picked date to date that will be converted on the API as
      // a noon of correct year-month-date combo in UTC
      const bookingStartForAPI = dateFromLocalToAPI(bookingStart);
      const bookingEndForAPI = dateFromLocalToAPI(bookingEnd);

      // Fetch speculated transaction for showing price in booking breakdown
      // NOTE: if unit type is line-item/units, quantity needs to be added.
      // The way to pass it to checkout page is through pageData.bookingData
      fetchSpeculatedTransaction(
        {
          listingId,
          bookingStart: bookingStartForAPI,
          bookingEnd: bookingEndForAPI,
          quantity: pageData.bookingData.quantity,
        },
        this.bookingAlias,
        this.state.appliedCoupon
      );
    }

    this.setState({
      pageData: pageData || {},
      dataLoaded: true,
      localQuantity: (pageData && pageData.bookingData && pageData.bookingData.quantity) || 1,
    });
  }

  handleQuantityChange(delta) {
    const newQty = Math.max(1, (this.state.localQuantity || 1) + delta);
    const { pageData } = this.state;

    // pageData.bookingData.quantity (not localQuantity) is what actually
    // gets submitted as the order's quantity (see handlePaymentIntent) and
    // what a page refresh restores from sessionStorage — without updating
    // it here, the stepper looked right but the real order kept whatever
    // quantity the page first loaded with, and a refresh reverted the
    // stepper back to that stale value too.
    const updatedPageData =
      pageData && pageData.bookingData
        ? { ...pageData, bookingData: { ...pageData.bookingData, quantity: newQty } }
        : pageData;

    if (updatedPageData && updatedPageData.listing && updatedPageData.bookingDates) {
      storeData(
        updatedPageData.bookingData,
        updatedPageData.bookingDates,
        updatedPageData.listing,
        updatedPageData.transaction,
        STORAGE_KEY
      );
    }

    this.setState({ localQuantity: newQty, pageData: updatedPageData }, () => {
      const couponInfo = this.state.appliedCoupon;
      this.refetchSpeculatedTransaction(couponInfo).then(result => {
        if (couponInfo && (!result || !result.ok)) {
          this.rejectCouponAndRefetch('Your coupon no longer applies at this quantity.');
        }
      });
    });
  }

  handlePaymentIntent(handlePaymentParams) {
    const {
      currentUser,
      stripeCustomerFetched,
      onInitiateOrder,
      onHandleCardPayment,
      onConfirmPayment,
      onSendMessage,
      onSavePaymentMethod,
    } = this.props;
    const {
      pageData,
      speculatedTransaction,
      message,
      paymentIntent,
      selectedPaymentMethod,
      saveAfterOnetimePayment,
    } = handlePaymentParams;
    const storedTx = ensureTransaction(pageData.transaction);

    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const ensuredStripeCustomer = ensureStripeCustomer(ensuredCurrentUser.stripeCustomer);
    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(
      ensuredStripeCustomer.defaultPaymentMethod
    );

    let createdPaymentIntent = null;

    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensuredStripeCustomer.attributes.stripeCustomerId &&
      ensuredDefaultPaymentMethod.id
    );
    const stripePaymentMethodId = hasDefaultPaymentMethod
      ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
      : null;

    const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);

    // Step 1: initiate order by requesting payment from Marketplace API
    const fnRequestPayment = fnParams => {
      // fnParams should be { listingId, bookingStart, bookingEnd }
      const hasPaymentIntents =
        storedTx.attributes.protectedData && storedTx.attributes.protectedData.stripePaymentIntents;

      // If paymentIntent exists, order has been initiated previously.
      return hasPaymentIntents
        ? Promise.resolve(storedTx)
        : onInitiateOrder(fnParams, storedTx.id, this.bookingAlias, this.state.appliedCoupon);
    };

    // Step 2: pay using Stripe SDK
    const fnHandleCardPayment = fnParams => {
      // fnParams should be returned transaction entity

      const order = ensureTransaction(fnParams);
      if (order.id) {
        // Store order.
        const { bookingData, bookingDates, listing } = pageData;
        storeData(bookingData, bookingDates, listing, order, STORAGE_KEY);
        this.setState({ pageData: { ...pageData, transaction: order } });
      }

      const hasPaymentIntents =
        order.attributes.protectedData && order.attributes.protectedData.stripePaymentIntents;

      if (!hasPaymentIntents) {
        throw new Error(
          `Missing StripePaymentIntents key in transaction's protectedData. Check that your transaction process is configured to use payment intents.`
        );
      }

      const { stripePaymentIntentClientSecret } = hasPaymentIntents
        ? order.attributes.protectedData.stripePaymentIntents.default
        : null;

      const { stripe, card, billingDetails, paymentIntent } = handlePaymentParams;
      const stripeElementMaybe = selectedPaymentFlow !== USE_SAVED_CARD ? { card } : {};

      // Note: payment_method could be set here for USE_SAVED_CARD flow.
      // { payment_method: stripePaymentMethodId }
      // However, we have set it already on API side, when PaymentIntent was created.
      const paymentParams =
        selectedPaymentFlow !== USE_SAVED_CARD
          ? {
              payment_method_data: {
                billing_details: billingDetails,
              },
            }
          : {};

      const params = {
        stripePaymentIntentClientSecret,
        orderId: order.id,
        stripe,
        ...stripeElementMaybe,
        paymentParams,
      };

      // If paymentIntent status is not waiting user action,
      // handleCardPayment has been called previously.
      const hasPaymentIntentUserActionsDone =
        paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);
      return hasPaymentIntentUserActionsDone
        ? Promise.resolve({ transactionId: order.id, paymentIntent })
        : onHandleCardPayment(params);
    };

    // Step 3: complete order by confirming payment to Marketplace API
    // Parameter should contain { paymentIntent, transactionId } returned in step 2
    const fnConfirmPayment = fnParams => {
      createdPaymentIntent = fnParams.paymentIntent;
      return onConfirmPayment(fnParams);
    };

    // Step 4: send initial message
    const fnSendMessage = fnParams => {
      return onSendMessage({ ...fnParams, message });
    };

    // Step 5: optionally save card as defaultPaymentMethod
    const fnSavePaymentMethod = fnParams => {
      const pi = createdPaymentIntent || paymentIntent;

      if (selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE) {
        return onSavePaymentMethod(ensuredStripeCustomer, pi.payment_method)
          .then(response => {
            if (response.errors) {
              return { ...fnParams, paymentMethodSaved: false };
            }
            return { ...fnParams, paymentMethodSaved: true };
          })
          .catch(e => {
            // Real error cases are catched already in paymentMethods page.
            return { ...fnParams, paymentMethodSaved: false };
          });
      } else {
        return Promise.resolve({ ...fnParams, paymentMethodSaved: true });
      }
    };

    // Here we create promise calls in sequence
    // This is pretty much the same as:
    // fnRequestPayment({...initialParams})
    //   .then(result => fnHandleCardPayment({...result}))
    //   .then(result => fnConfirmPayment({...result}))
    const applyAsync = (acc, val) => acc.then(val);
    const composeAsync = (...funcs) => x => funcs.reduce(applyAsync, Promise.resolve(x));
    const handlePaymentIntentCreation = composeAsync(
      fnRequestPayment,
      fnHandleCardPayment,
      fnConfirmPayment,
      fnSendMessage,
      fnSavePaymentMethod
    );

    // Create order aka transaction
    // NOTE: if unit type is line-item/units, quantity needs to be added.
    // The way to pass it to checkout page is through pageData.bookingData
    const tx = speculatedTransaction ? speculatedTransaction : storedTx;

    // Note: optionalPaymentParams contains Stripe paymentMethod,
    // but that can also be passed on Step 2
    // stripe.handleCardPayment(stripe, { payment_method: stripePaymentMethodId })
    const optionalPaymentParams =
      selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethod
        ? { paymentMethod: stripePaymentMethodId }
        : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
        ? { setupPaymentMethodForSaving: true }
        : {};

    const orderParams = {
      listingId: pageData.listing.id,
      bookingStart: tx.booking.attributes.start,
      bookingEnd: tx.booking.attributes.end,
      quantity: pageData.bookingData.quantity,
      ...optionalPaymentParams,
    };

    return handlePaymentIntentCreation(orderParams);
  }

  handleSubmit(values) {
    if (this.state.submitting) {
      return;
    }
    this.setState({ submitting: true });

    const { history, speculatedTransaction, currentUser, paymentIntent, dispatch } = this.props;
    const { card, message, paymentMethod, formValues } = values;
    const {
      name,
      addressLine1,
      addressLine2,
      postal,
      city,
      state,
      country,
      saveAfterOnetimePayment,
    } = formValues;

    // Billing address is recommended.
    // However, let's not assume that <StripePaymentAddress> data is among formValues.
    // Read more about this from Stripe's docs
    // https://stripe.com/docs/stripe-js/reference#stripe-handle-card-payment-no-element
    const addressMaybe =
      addressLine1 && postal
        ? {
            address: {
              city: city,
              country: country,
              line1: addressLine1,
              line2: addressLine2,
              postal_code: postal,
              state: state,
            },
          }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const requestPaymentParams = {
      pageData: this.state.pageData,
      speculatedTransaction,
      stripe: this.stripe,
      card,
      billingDetails,
      message,
      paymentIntent,
      selectedPaymentMethod: paymentMethod,
      saveAfterOnetimePayment: !!saveAfterOnetimePayment,
    };

    this.handlePaymentIntent(requestPaymentParams)
      .then(res => {
        const { orderId, messageSuccess, paymentMethodSaved } = res;
        this.setState({ submitting: false });

        const routes = routeConfiguration();
        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
        const orderDetailsPath = pathByRouteName('OrderDetailsPage', routes, { id: orderId.uuid });
        const initialValues = {
          initialMessageFailedToTransaction,
          savePaymentMethodFailed: !paymentMethodSaved,
        };

        initializeOrderPage(initialValues, routes, dispatch);
        clearData(STORAGE_KEY);
        history.push(orderDetailsPath);
      })
      .catch(err => {
        console.error(err);
        this.setState({ submitting: false });
      });
  }

  onStripeInitialized(stripe) {
    this.stripe = stripe;

    const { paymentIntent, onRetrievePaymentIntent } = this.props;
    const tx = this.state.pageData ? this.state.pageData.transaction : null;

    // We need to get up to date PI, if booking is created but payment is not expired.
    const shouldFetchPaymentIntent =
      this.stripe &&
      !paymentIntent &&
      tx &&
      tx.id &&
      tx.booking &&
      tx.booking.id &&
      txIsPaymentPending(tx) &&
      !checkIsPaymentExpired(tx);

    if (shouldFetchPaymentIntent) {
      const { stripePaymentIntentClientSecret } =
        tx.attributes.protectedData && tx.attributes.protectedData.stripePaymentIntents
          ? tx.attributes.protectedData.stripePaymentIntents.default
          : {};

      // Fetch up to date PaymentIntent from Stripe
      onRetrievePaymentIntent({ stripe, stripePaymentIntentClientSecret });
    }
  }

  render() {
    const {
      scrollingDisabled,
      speculateTransactionInProgress,
      speculateTransactionError,
      speculatedTransaction: speculatedTransactionMaybe,
      initiateOrderError,
      confirmPaymentError,
      intl,
      params,
      currentUser,
      handleCardPaymentError,
      paymentIntent,
      retrievePaymentIntentError,
      stripeCustomerFetched,
    } = this.props;

    // console.log({currentUser});

    // Since the listing data is already given from the ListingPage
    // and stored to handle refreshes, it might not have the possible
    // deleted or closed information in it. If the transaction
    // initiate or the speculative initiate fail due to the listing
    // being deleted or closec, we should dig the information from the
    // errors and not the listing data.
    const listingNotFound =
      isTransactionInitiateListingNotFoundError(speculateTransactionError) ||
      isTransactionInitiateListingNotFoundError(initiateOrderError);

    const isLoading = !this.state.dataLoaded || speculateTransactionInProgress;

    const { listing, bookingDates, transaction } = this.state.pageData;
    const existingTransaction = ensureTransaction(transaction);
    const speculatedTransaction = ensureTransaction(speculatedTransactionMaybe, {}, null);
    const currentListing = ensureListing(listing);
    const currentAuthor = ensureUser(currentListing.author);

    const listingTitle = currentListing.attributes.title;
    const title = intl.formatMessage({ id: 'CheckoutPage.title' }, { listingTitle });

    const pageProps = { title, scrollingDisabled };
    const topbar = (
      <div className={css.topbar}>
        <NamedLink className={css.home} name="LandingPage">
          <img src={marketplaceLockupLogoDarkText} alt="Viveka" style={{ height: '40px' }} />
        </NamedLink>
      </div>
    );

    if (isLoading) {
      return <Page {...pageProps}>{topbar}</Page>;
    }

    const isOwnListing =
      currentUser &&
      currentUser.id &&
      currentAuthor &&
      currentAuthor.id &&
      currentAuthor.id.uuid === currentUser.id.uuid;

    const hasListingAndAuthor = !!(currentListing.id && currentAuthor.id);
    const hasBookingDates = !!(
      bookingDates &&
      bookingDates.bookingStart &&
      bookingDates.bookingEnd
    );
    const hasRequiredData = hasListingAndAuthor && hasBookingDates;
    const canShowPage = hasRequiredData && !isOwnListing;
    const shouldRedirect = !isLoading && !canShowPage;

    // Redirect back to ListingPage if data is missing.
    // Redirection must happen before any data format error is thrown (e.g. wrong currency)
    if (shouldRedirect) {
      // eslint-disable-next-line no-console
      console.error('Missing or invalid data for checkout, redirecting back to listing page.', {
        transaction: speculatedTransaction,
        bookingDates,
        listing,
      });
      return <NamedRedirect name="ListingPage" params={params} />;
    }

    // Show breakdown only when speculated transaction and booking are loaded
    // (i.e. have an id)
    const tx = existingTransaction.booking ? existingTransaction : speculatedTransaction;
    const txBooking = ensureBooking(tx.booking);
    const breakdown =
      tx.id && txBooking.id ? (
        <BookingBreakdown
          className={css.bookingBreakdown}
          userRole="customer"
          unitType={config.bookingUnitType}
          transaction={tx}
          booking={txBooking}
          dateType={DATE_TYPE_DATE}
          listing={currentListing}
        />
      ) : null;

    const isPaymentExpired = checkIsPaymentExpired(existingTransaction);
    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
      ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id
    );

    // Allow showing page when currentUser is still being downloaded,
    // but show payment form only when user info is loaded.
    const showPaymentForm = !!(
      currentUser &&
      hasRequiredData &&
      !listingNotFound &&
      !initiateOrderError &&
      !speculateTransactionError &&
      !retrievePaymentIntentError &&
      !isPaymentExpired
    );

    const listingLink = (
      <NamedLink
        name="ListingPage"
        params={{ id: currentListing.id.uuid, slug: createSlug(listingTitle) }}
      >
        <FormattedMessage id="CheckoutPage.errorlistingLinkText" />
      </NamedLink>
    );

    const isAmountTooLowError = isTransactionInitiateAmountTooLowError(initiateOrderError);
    const isChargeDisabledError = isTransactionChargeDisabledError(initiateOrderError);
    const isBookingTimeNotAvailableError = isTransactionInitiateBookingTimeNotAvailableError(
      initiateOrderError
    );
    const stripeErrors = transactionInitiateOrderStripeErrors(initiateOrderError);

    let initiateOrderErrorMessage = null;
    let listingNotFoundErrorMessage = null;

    if (listingNotFound) {
      listingNotFoundErrorMessage = (
        <p className={css.notFoundError}>
          <FormattedMessage id="CheckoutPage.listingNotFoundError" />
        </p>
      );
    } else if (isAmountTooLowError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
        </p>
      );
    } else if (isBookingTimeNotAvailableError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
        </p>
      );
    } else if (isChargeDisabledError) {
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.chargeDisabledMessage" />
        </p>
      );
    } else if (stripeErrors && stripeErrors.length > 0) {
      // NOTE: Error messages from Stripes are not part of translations.
      // By default they are in English.
      const stripeErrorsAsString = stripeErrors.join(', ');
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage
            id="CheckoutPage.initiateOrderStripeError"
            values={{ stripeErrors: stripeErrorsAsString }}
          />
        </p>
      );
    } else if (initiateOrderError) {
      // Generic initiate order error
      initiateOrderErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderError" values={{ listingLink }} />
        </p>
      );
    }

    const speculateTransactionErrorMessage = speculateTransactionError ? (
      <p className={css.speculateError}>
        <FormattedMessage id="CheckoutPage.speculateTransactionError" />
      </p>
    ) : null;
    let speculateErrorMessage = null;

    if (isTransactionInitiateMissingStripeAccountError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.providerStripeAccountMissingError" />
        </p>
      );
    } else if (isTransactionInitiateBookingTimeNotAvailableError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.bookingTimeNotAvailableMessage" />
        </p>
      );
    } else if (isTransactionZeroPaymentError(speculateTransactionError)) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.initiateOrderAmountTooLow" />
        </p>
      );
    } else if (speculateTransactionError) {
      speculateErrorMessage = (
        <p className={css.orderError}>
          <FormattedMessage id="CheckoutPage.speculateFailedMessage" />
        </p>
      );
    }

    // const unitType = config.bookingUnitType;
    // const isNightly = unitType === LINE_ITEM_NIGHT;
    // const isDaily = unitType === LINE_ITEM_DAY;

    // const unitTranslationKey = isNightly
    //   ? 'CheckoutPage.perNight'
    //   : isDaily
    //   ? 'CheckoutPage.perDay'
    //   : 'CheckoutPage.perUnit';

    const price = currentListing.attributes.price;
    const formattedPrice = formatMoney(intl, price);
    const detailsSubTitle = `${formattedPrice} per hour`;

    const showInitialMessageInput = !(
      existingTransaction && existingTransaction.attributes.lastTransition === TRANSITION_ENQUIRE
    );

    // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
    const userName =
      currentUser && currentUser.attributes
        ? `${currentUser.attributes.profile.firstName} ${currentUser.attributes.profile.lastName}`
        : null;

    // If paymentIntent status is not waiting user action,
    // handleCardPayment has been called previously.
    const hasPaymentIntentUserActionsDone =
      paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

    // If your marketplace works mostly in one country you can use initial values to select country automatically
    // e.g. {country: 'FI'}

    const initalValuesForStripePayment = { name: userName };

    // ── Dynamic price data for right summary panel ──
    const isHourly =
      currentListing.attributes.publicData &&
      currentListing.attributes.publicData.per === 'hourly';
    const { localQuantity } = this.state;
    const priceAmount = price ? price.amount / 100 : 0;
    const lineTotal = priceAmount * localQuantity;
    const speculatedLineItems =
      speculatedTransaction && speculatedTransaction.attributes
        ? speculatedTransaction.attributes.lineItems || []
        : [];
    const customerCommissionItem = speculatedLineItems.find(
      li => li.code === 'line-item/customer-commission' && !li.reversal
    );
    const platformFeeAmount = customerCommissionItem
      ? customerCommissionItem.lineTotal.amount
      : 0;
    const hasMinimumTransactionAdjustment = speculatedLineItems.some(
      li => li.code === 'line-item/minimum-price-adjustment' && !li.reversal
    );
    const payinTotal =
      speculatedTransaction && speculatedTransaction.attributes && speculatedTransaction.attributes.payinTotal
        ? speculatedTransaction.attributes.payinTotal
        : null;
    const payinFormatted = payinTotal ? formatMoney(intl, payinTotal) : `$${lineTotal.toFixed(2)}`;

    const coachName = currentAuthor.attributes.profile.displayName || '';
    const listingTag = (currentListing.attributes.publicData?.typeOfService || [])[0] || '';

    const listingInitials = listingTitle
      .split(' ')
      .map(w => w[0])
      .filter(Boolean)
      .slice(0, 3)
      .join('')
      .toUpperCase();

    const checkoutTopbar = (
      <div className={css.checkoutTopbar}>
        <NamedLink className={css.topbarLogo} name="LandingPage">
          <img src={marketplaceLockupLogoDarkText} alt="Viveka" className={css.topbarLogoImg} />
        </NamedLink>
        <div className={css.topbarSecure}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <rect x="5" y="11" width="14" height="10" rx="2" />
            <path d="M8 11V7a4 4 0 0 1 8 0v4" />
          </svg>
          Secure checkout
        </div>
        <NamedLink
          className={css.topbarBack}
          name="ListingPage"
          params={{ id: currentListing.id.uuid, slug: createSlug(listingTitle) }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M15 18l-6-6 6-6" />
          </svg>
          Back to session
        </NamedLink>
      </div>
    );

    const topLevelErrorMessages = (
      <React.Fragment>
        {initiateOrderErrorMessage}
        {listingNotFoundErrorMessage}
        {speculateErrorMessage}
        {speculateTransactionErrorMessage}
        {retrievePaymentIntentError ? (
          <p className={css.errorMsg}>
            <FormattedMessage
              id="CheckoutPage.retrievingStripePaymentIntentFailed"
              values={{ listingLink }}
            />
          </p>
        ) : null}
        {isPaymentExpired ? (
          <p className={css.errorMsg}>
            <FormattedMessage id="CheckoutPage.paymentExpiredMessage" values={{ listingLink }} />
          </p>
        ) : null}
      </React.Fragment>
    );

    const listingInfoCard = (
      <div className={css.sectionCard}>
        <div className={css.sectionHeader}>
          <span className={css.sectionNum}>01</span>
          <h3 className={css.sectionTitle}>What you're booking</h3>
        </div>

        <div className={css.heroBanner}>
          <span className={css.heroBannerCircle1} />
          <span className={css.heroBannerCircle2} />
          <h2 className={css.heroBannerTitle}>{listingTitle}</h2>
          {listingTag && <p className={css.heroBannerSubtitle}>{listingTag}</p>}
        </div>

        <h4 className={css.listingHeading}>{listingTitle}</h4>
        <p className={css.hostedByLine}>
          Hosted by <strong>{coachName}</strong>
        </p>

        {currentListing.attributes.description && (
          <React.Fragment>
            <div
              className={[
                css.descriptionBody,
                this.state.showFullDescription
                  ? css.descriptionBodyExpanded
                  : css.descriptionBodyCollapsed,
              ].join(' ')}
            >
              {ReactHtmlParser(decode(currentListing.attributes.description))}
            </div>
            <button
              type="button"
              className={css.readMoreBtn}
              onClick={() =>
                this.setState(prev => ({
                  showFullDescription: !prev.showFullDescription,
                }))
              }
            >
              {this.state.showFullDescription ? 'Show less' : 'Show more'}
              <FontAwesomeIcon
                icon={faChevronDown}
                className={[
                  css.readMoreIcon,
                  this.state.showFullDescription ? css.readMoreIconOpen : '',
                ].join(' ')}
              />
            </button>
          </React.Fragment>
        )}
      </div>
    );

    const bookingSummaryCard = (
      <div className={css.summaryCard}>
        <div className={css.summaryBadgeRow}>
          <span className={css.summaryBadge}>{listingInitials}</span>
          <div className={css.summaryBadgeInfo}>
            <p className={css.summaryBadgeTitle}>{listingTitle}</p>
            <p className={css.summaryBadgeSub}>1-on-1 with {coachName}</p>
          </div>
        </div>

        <div className={css.summarySep} />

        {isHourly && (
          <div className={css.summaryRow}>
            <span className={css.summaryRowLabel}>Hours</span>
            <div className={css.stepper_qty}>
              <button
                type="button"
                className={css.stepperBtn}
                onClick={() => this.handleQuantityChange(-1)}
                disabled={localQuantity <= 1}
              >
                −
              </button>
              <span className={css.stepperVal}>{localQuantity}</span>
              <button
                type="button"
                className={css.stepperBtn}
                onClick={() => this.handleQuantityChange(1)}
              >
                +
              </button>
            </div>
          </div>
        )}

        <div className={css.summaryRow}>
          <span className={css.summaryRowLabel}>
            {isHourly
              ? `${formattedPrice} × ${localQuantity} hour${localQuantity !== 1 ? 's' : ''}`
              : 'Package price'}
          </span>
          <span className={css.summaryRowValue}>${lineTotal.toFixed(2)}</span>
        </div>

        <div className={css.summaryRow}>
          <span className={css.summaryRowLabel}>Platform fee</span>
          <span className={platformFeeAmount === 0 ? css.feeFree : css.summaryRowValue}>
            {platformFeeAmount === 0
              ? 'Free'
              : formatMoney(intl, customerCommissionItem.lineTotal)}
          </span>
        </div>

        <div className={css.summarySep} />

        <div className={css.summaryTotalRow}>
          <span className={css.summaryTotalLabel}>Total</span>
          <span className={css.summaryTotalValue}>
            {speculateTransactionInProgress ? '…' : payinFormatted}
          </span>
        </div>
        <p className={css.vatHint}>VAT calculated at confirmation</p>
        {hasMinimumTransactionAdjustment && (
          <p className={css.minimumTransactionHint}>Minimum transaction is $10.</p>
        )}

        {this.state.showPromoInput || this.state.appliedCoupon ? (
          <React.Fragment>
            <div className={css.promoRow}>
              <input
                className={css.promoInput}
                placeholder="Coupon code"
                value={this.state.promoCode}
                disabled={!!this.state.appliedCoupon || this.state.promoApplying}
                onChange={e => this.setState({ promoCode: e.target.value, promoError: null })}
              />
              {this.state.appliedCoupon ? (
                <button type="button" className={css.promoBtn} onClick={this.onRemovePromo}>
                  Remove
                </button>
              ) : (
                <button
                  type="button"
                  className={css.promoBtn}
                  onClick={this.onApplyPromo}
                  disabled={this.state.promoApplying || !this.state.promoCode.trim()}
                >
                  {this.state.promoApplying ? 'Checking…' : 'Apply'}
                </button>
              )}
            </div>
            {this.state.promoError && <p className={css.promoErrorText}>{this.state.promoError}</p>}
            {this.state.appliedCoupon && (
              <p className={css.promoSuccessText}>
                Coupon {this.state.appliedCoupon.code} applied —{' '}
                {this.state.appliedCoupon.discountType === 'percent'
                  ? `${this.state.appliedCoupon.discountValue}% off`
                  : `$${this.state.appliedCoupon.discountValue} off`}
              </p>
            )}
          </React.Fragment>
        ) : (
          <button
            type="button"
            className={css.promoToggle}
            onClick={() => this.setState({ showPromoInput: true })}
          >
            Add a coupon code
          </button>
        )}
      </div>
    );

    const trustBullets = (
      <div className={css.trustBadges}>
        <p className={css.trustItem}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          Your card is only charged once {coachName.split(' ')[0]} accepts.
        </p>
        <p className={css.trustItem}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          Cancel free of charge up to 24 hours before the session.
        </p>
        <p className={css.trustItem}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5"><polyline points="20 6 9 17 4 12" /></svg>
          Covered by Viveka buyer protection.
        </p>
      </div>
    );

    return (
      <PageWrapper>
        <div className={css.checkoutPage}>
          {checkoutTopbar}

          <div className={css.pageIntro}>
            <h1 className={css.pageTitle}>Complete your booking</h1>
            <p className={css.pageSubtitle}>
              Booking as <strong>{userName || 'you'}</strong> ·{' '}
              <NamedLink
                className={css.changeLink}
                name="ListingPage"
                params={{ id: currentListing.id.uuid, slug: createSlug(listingTitle) }}
              >
                Change
              </NamedLink>
            </p>
            {topLevelErrorMessages}
          </div>

          {showPaymentForm ? (
            <div className={css.checkoutLayout}>
              <StripePaymentForm
                onSubmit={this.handleSubmit}
                inProgress={this.state.submitting}
                formId="CheckoutPagePaymentForm"
                authorDisplayName={coachName}
                showInitialMessageInput={showInitialMessageInput}
                initialValues={initalValuesForStripePayment}
                initiateOrderError={initiateOrderError}
                handleCardPaymentError={handleCardPaymentError}
                confirmPaymentError={confirmPaymentError}
                hasHandledCardPayment={hasPaymentIntentUserActionsDone}
                loadingData={!stripeCustomerFetched}
                defaultPaymentMethod={
                  hasDefaultPaymentMethod
                    ? currentUser.stripeCustomer.defaultPaymentMethod
                    : null
                }
                paymentIntent={paymentIntent}
                onStripeInitialized={this.onStripeInitialized}
                totalDueFormatted={speculateTransactionInProgress ? '…' : payinFormatted}
                listingInfoCard={listingInfoCard}
                bookingSummaryCard={bookingSummaryCard}
                trustBullets={trustBullets}
              />
            </div>
          ) : (
            <div className={css.checkoutLayoutFallback}>
              {listingInfoCard}
              {bookingSummaryCard}
            </div>
          )}
        </div>

        <ContainerWrapper>
          <Footer />
        </ContainerWrapper>
      </PageWrapper>
    );
  }
}

CheckoutPageComponent.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  bookingData: {},
  bookingDates: null,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
};

CheckoutPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  bookingData: object,
  bookingDates: shape({
    bookingStart: instanceOf(Date).isRequired,
    bookingEnd: instanceOf(Date).isRequired,
  }),
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onHandleCardPayment: func.isRequired,
  onRetrievePaymentIntent: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  onValidateCoupon: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,
  // handleCardPaymentError comes from Stripe so that's why we can't expect it to be in a specific form
  handleCardPaymentError: oneOfType([propTypes.error, object]),
  paymentIntent: object,

  // from connect
  dispatch: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
};

const mapStateToProps = state => {
  const {
    listing,
    bookingData,
    bookingDates,
    stripeCustomerFetched,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    initiateOrderError,
    confirmPaymentError,
  } = state.CheckoutPage;
  const { currentUser } = state.user;
  const { handleCardPaymentError, paymentIntent, retrievePaymentIntentError } = state.stripe;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    stripeCustomerFetched,
    bookingData,
    bookingDates,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    listing,
    initiateOrderError,
    handleCardPaymentError,
    confirmPaymentError,
    paymentIntent,
    retrievePaymentIntentError,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  fetchSpeculatedTransaction: (params, type, couponInfo) =>
    dispatch(speculateTransaction(params, type, couponInfo)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onInitiateOrder: (params, transactionId, type, couponInfo) =>
    dispatch(initiateOrder(params, transactionId, type, couponInfo)),
  onValidateCoupon: data => dispatch(validateCoupon(data)),
  onRetrievePaymentIntent: params => dispatch(retrievePaymentIntent(params)),
  onHandleCardPayment: params => dispatch(handleCardPayment(params)),
  onConfirmPayment: params => dispatch(confirmPayment(params)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onSavePaymentMethod: (stripeCustomer, stripePaymentMethodId) =>
    dispatch(savePaymentMethod(stripeCustomer, stripePaymentMethodId)),
});

const CheckoutPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CheckoutPageComponent);

CheckoutPage.setInitialValues = initialValues => setInitialValues(initialValues);

CheckoutPage.displayName = 'CheckoutPage';

export default CheckoutPage;
